import classNames from 'classnames';
import { DateTime } from 'luxon';
import * as React from 'react';
import {
	getFormValues,
	InjectedFormProps,
	reduxForm,
	SubmissionError,
} from 'redux-form';

import { TestingLocation, PrepaidItem } from '@aicpcu/web-api-spec-model';

import { AddCartButton } from '@app/components/add-cart-button';
import { PrimaryButton } from '@app/components/button';
import { PurchaseExamOverlay } from '@app/components/card-tab-overlay';
import { FormContainer, Select } from '@app/components/forms';
import {
	examTestingLocationRequired,
	examWindowRequired,
} from '@app/components/forms/validations.constants';
import { Loader } from '@app/components/loader';
import { CeModal } from '@app/components/modal/ce-modal';
import { Notification } from '@app/components/notification';
import { DOMESTIC_COUNTRY_VALUE } from '@app/constants/address';
import { FormValues } from '@app/interfaces/form';
import { Store } from '@app/interfaces/store/store';
import { webApi } from '@app/services/web-api';
import { getCurrentDateTime, parseDateWithoutTimezone } from '@app/utility/date';
import { gtmAddToCart } from '@app/utility/gtm';

import {
	PURCHASE_TAB_STRINGS,
	TabIdType,
	TestingLocations,
} from './purchase-form.constants';
import {
	ExamData,
	ExamDataOption,
	ExamDataStateKey,
	ExamExamWindowOptions,
	GroupedExamOptions,
	PropExamRadioGroup,
} from './purchase-form.types';
import {
	formUtil,
	localStorageUtil,
	redirectToLogin,
	tabUtil,
} from './purchase-form.util';
import {
	getCountryOptions,
	getLabel,
	getProgramOptions,
	getStateOptions,
	getTestingCenterOptions,
	getTestingLocationNote,
} from './purchase-tab-exam.util';
import {
	TestingLocationForm,
	TestingLocationFormComponent,
} from './testing-location-form';

type PrepaidExamItem = PrepaidItem & {
	hasFailedExam?: boolean;
};

/** PPA enrollment payload */
interface PpaEnrollmentPayload {
	prepaidAgreementId: string | number;
	licenseId: string | number;
	itemId: number;
	targetSeries: string;
	programCode?: string;
	ceRequested?: boolean;
}

/** PPA enrollment result */
interface PpaEnrollmentResult {
	type?: string;
	payload?: {
		detail?: string;
		[key: string]: any;
	};
	[key: string]: any;
}

type PropPurchaseTabExam = {
	actions: any;
	courseCode: string;
	description: string;
	examWindowOptions?: ExamExamWindowOptions[];
	hasNoExamWindows?: boolean;
	hidden: boolean;
	id: number;
	isSingleTab: boolean;
	note?: string;
	onSelect: () => void;
	setContainerHeight: (value: string, tabId: TabIdType) => void;
	store: Store;
	// EXPPA-25: PPA support for exam enrollments
	// Array of exam prepaid items from user's PPA licenses
	examPrepaidItems?: PrepaidExamItem[];
	// PPA enrollment handler
	onPpaEnroll?: (payload: PpaEnrollmentPayload) => Promise<PpaEnrollmentResult>;
};

const { EXAM_TAB } = PURCHASE_TAB_STRINGS;

/**
 * Login button. Sets local storage for a scroll journey after login redirect.
 *
 * @returns {React.ReactElement} Login button component.
 */
const LoginButton = ({
	courseCode,
	loginUrl,
}: {
	courseCode: string;
	loginUrl: string;
}): React.ReactElement => {
	const onLogin = () => {
		localStorageUtil.saveTab(EXAM_TAB.id, courseCode);
		redirectToLogin(loginUrl, `course-code-${courseCode}`);
	};
	return <PrimaryButton onClick={() => onLogin()}>Login</PrimaryButton>;
};

/**
 * Get exam type options by active exam window.
 *
 * @param {GroupedExamOptions} options Exam window options.
 *
 * @returns {Object} Selected exam options grouped by type.
 */
const splitExamOptions = (
	options?: GroupedExamOptions,
): PropExamRadioGroup => ({
	PROMETRIC: {
		label: 'Prometric',
		value: 'PROMETRIC',
		options: options?.PROMETRIC || [],
	},
	ONSITE: {
		label: 'On-site',
		value: 'ONSITE',
		options: options?.INTERNET || [],
	},
	VIRTUAL: {
		label: 'Virtual',
		value: 'VIRTUAL',
		options: options?.VIRTUAL || [],
	},
});

/**
 * Purchase Exam Form is Redux Form.
 *
 * Depending on an exam window selection, a radio group of exam location types
 * will be available. Upon successful form submission, an overlay is presented.
 *
 * Any parameter with "Redux or Redux Form" descriptor is connected and
 * composed.
 *
 * @param {object} actions              Redux Actions.
 * @param {number} courseCode           Parent course's code.
 * @param {object} examWindowOptions    Exam window options for select option.
 * @param {boolean} hasNoExamWindows        Has no exam windows. Defaults to first window.
 * @callback handleSubmit               Redux form handleSubmit callback.
 * @callback onSelect                   Handle overlay tab selection.
 * @callback reset                      Redux form reset function.
 *
 * @returns {React.Element} Purchase exam form component.
 */
const PurchaseTabExamRaw: React.FC<
	InjectedFormProps<{}, PropPurchaseTabExam> & PropPurchaseTabExam
> = ({
	actions: { setApiError, addToCart, addExamToCart, getCart },
	courseCode,
	description,
	examWindowOptions,
	hasNoExamWindows = false,
	hidden,
	id,
	isSingleTab,
	onSelect,
	setContainerHeight,
	store,
	note = '',
	// EXPPA-25: PPA support props
	examPrepaidItems = [],
	onPpaEnroll,
	...reduxFormProps
}: InjectedFormProps<{}, PropPurchaseTabExam> & PropPurchaseTabExam) => {
	const {
		change,
		form,
		handleSubmit,
		reset,
		submitFailed,
		submitSucceeded,
		submitting,
		valid,
	} = reduxFormProps;

	const panelId = tabUtil.setPanelId(EXAM_TAB.id, id);

	// Fetch form values.
	const formValues: FormValues = getFormValues(form)(store);

	const fieldNames = Object.freeze(formUtil.createExamFields(id));
	const {
		EXAMWINDOW_FIELDNAME,
		TESTING_LOCATION_FIELDNAME,
		TESTING_LOCATION_FIELDS,
	} = fieldNames;

	// Form value for: Exam Window.
	const selectedExamWindow = formUtil.selectValue(
		formValues,
		EXAMWINDOW_FIELDNAME,
	);

	// Form value for: Testing Location.
	const selectedTestingLocation = formUtil.selectValue(
		formValues,
		TESTING_LOCATION_FIELDNAME,
	);

	// Form value for: Exam.
	const selectedExam = TestingLocations.guard(selectedTestingLocation)
		? formUtil.selectValue(
				formValues,
				TESTING_LOCATION_FIELDS[selectedTestingLocation].EXAM_FIELDNAME,
		  )
		: '';

	// Determine exam options if a window is selected.
	const { options } =
		examWindowOptions?.find(({ value }) => value === selectedExamWindow) || {};
	const examOptions = splitExamOptions(options);

	// Find and deconstruct the selected exam option, if an exam option is selected.
	const selectedExamOption = TestingLocations.guard(selectedTestingLocation)
		? examOptions[selectedTestingLocation].options.find(
				({ value }) => value === selectedExam,
		  )
		: undefined;

	const {
		salePrice,
		label: selectedTitle = '',
		examDate = '',
		currentPrice,
		examWindowStartDate,
	} = selectedExamOption || { salePrice: 0, currentPrice: 0 };

	const examStartDate = examWindowStartDate
		? DateTime.fromFormat(examWindowStartDate, 'yyyy-MM-dd')
		: undefined;

	const shouldApplySalePrice = examStartDate
		? DateTime.now().toMillis() < examStartDate.toMillis()
		: false;

	const priceToDisplay = shouldApplySalePrice ? salePrice : currentPrice;

	// EXPPA-25: Check PPA coverage dynamically based on selected exam
	// EXPPA-38: Exclude retakes and failed exams from PPA enrollment
	const examPpaData = React.useMemo(() => {
		if (!selectedExam) {
			return {
				hasCoverage: false,
				licenseId: null,
				item: null,
				isRetake: false,
				hasFailedExam: false,
				enrolled: false,
			};
		}

		const itemId = parseInt(selectedExam, 10);

		// EXPPA-38: Find any matching PPA item (regardless of showEnroll status)
		// to detect retake scenarios
		const anyPpaItem = examPrepaidItems.find(
			(item) => item.itemId === itemId,
		);

		// EXPPA-38: Check if this is a retake attempt
		// A retake is when the user is currently enrolled AND has failed the exam
		// ONLY failed exams are considered retakes that need to use cart purchase
		const isRetake = anyPpaItem?.enrolled === true && anyPpaItem?.hasFailedExam === true;

		// EXPPA-38: Check if user has failed this exam (cannot re-enroll via prepaid)
		const hasFailedExam = anyPpaItem?.hasFailedExam === true;

		// EXPPA-38: Extract enrolled status from prepaid item
		const enrolled = anyPpaItem?.enrolled === true;

		// Only show PPA enrollment if showEnroll is true (not a retake)
		const coveredItem = examPrepaidItems.find(
			(item) => item.itemId === itemId && item.showEnroll === true,
		);

		return {
			// EXPPA-38: Disable PPA coverage for retakes and failed exams
			hasCoverage: !!coveredItem && !isRetake && !hasFailedExam,
			licenseId: anyPpaItem?.licenseId || coveredItem?.licenseId,
			item: anyPpaItem || coveredItem,
			isRetake,
			hasFailedExam,
			enrolled,
		};
	}, [selectedExam, examPrepaidItems]);

	const {
		hasCoverage: hasPpaCoverage,
		licenseId: ppaLicenseId,
		item: ppaItem,
		isRetake: isPpaRetake,
		hasFailedExam,
		enrolled,
	} = examPpaData;

	// Extract CE and program requirements from ppaItem
	// ceRequired: CE must be selected to enroll
	// ceEligible (from ppaItem): CE is available but optional
	const ceRequired = ppaItem?.ceRequired || false;
	const programRequired = ppaItem?.programRequired ?? false;

	// EXPPA-25: Check if today is the last day of the selected exam window
	// Also check if the window has already closed (current date > end date)
	const selectedWindowData = React.useMemo(() => {
		if (!selectedExamWindow) {
			return { isLastDay: false, isWindowClosed: false, endDate: null };
		}

		const window = examWindowOptions?.find(
			({ value }) => value === selectedExamWindow,
		);

		const currentDate = getCurrentDateTime().toISODate() ?? '';
		// Convert endDate to ISO string for comparison, always normalizing to YYYY-MM-DD format
		let endDateString = '';
		if (window?.endDate) {
			endDateString = parseDateWithoutTimezone(window.endDate)?.toISODate() ?? '';
		}
		const isLastDay =
			endDateString &&
			currentDate &&
			currentDate === endDateString;
		const isWindowClosed =
			endDateString &&
			currentDate &&
			currentDate > endDateString;

		return {
			isLastDay: !!isLastDay,
			isWindowClosed: !!isWindowClosed,
			endDate: window?.endDate,
		};
	}, [selectedExamWindow, examWindowOptions]);

	// Determine if there are matching items and exam windows in cart.
	const hasMatchingCartItem = store.cart.cartItems.some(
		({ courseCode: cartCourseCode = '', inventoryClass = '' }) =>
			(inventoryClass === 'PK' && cartCourseCode === courseCode) ||
			(inventoryClass === 'SV' && !cartCourseCode),
	);

	const hasMatchingExamWindow = (() => {
		const matchingMaterial = store.cart.cartItems.find(
			({ courseCode: cartCourseCode = '', inventoryClass = '' }) =>
				inventoryClass === 'PK' && cartCourseCode === courseCode,
		);

		const { targetSeries = '' } = matchingMaterial || {};
		return (
			matchingMaterial &&
			selectedExamWindow &&
			targetSeries !== selectedExamWindow
		);
	})();

	// State for submit error.
	const [submitError, setSubmitError] = React.useState<unknown>();

	const [storedTitle, setStoredTitle] = React.useState('');
	const [storedIsPpaEnrollment, setStoredIsPpaEnrollment] = React.useState(false);

	const [labels, setLabels] = React.useState({
		examWindow: '',
		locationType: '',
		exam: '',
		program: '',
		country: '',
		state: '',
		center: '',
	});

	// EXPPA-25: CE Modal state for PPA enrollments
	// Store as number to match backend expectations and other enrollment flows
	const [ceGroupId, setCeGroupId] = React.useState<number | null>(null);

	// Wrapper to normalize groupId to number when setting
	const setCeGroupIdNormalized = React.useCallback((groupId: string | number | null) => {
		if (groupId === null || groupId === undefined) {
			setCeGroupId(null);
		} else {
			// Coerce to number like other enrollment flows do
			setCeGroupId(+groupId);
		}
	}, []);

	const [locationOptions, setLocationOptions] = React.useState<
		readonly TestingLocation[]
	>([]);

	const initialExamDatum = { options: [], loading: false };
	const initialExamData = {
		centers: initialExamDatum,
		countries: initialExamDatum,
		programs: initialExamDatum,
		states: initialExamDatum,
	};

	const [examData, setExamData] = React.useState<ExamData>(initialExamData);
	const { centers, countries, programs, states } = examData;

	const showTestingLocationForm = Object.values(examOptions).some(
		({ options: opt }) => opt.length,
	);

	/**
	 * Resets Redux form fields defined by an array of fieldnames.
	 *
	 * @param {string[]} fields Redux form field names to reset.
	 */
	const resetFields = (...fields: (string | undefined)[]) =>
		fields.forEach((name) => name !== undefined && change(name, ''));

	/**
	 * Reset exam data to initial state for keys provided.
	 *
	 * If examDataKeys is empty, then reset the whole ExamData state.
	 *
	 * @param {ExamDataStateKey[]} examDataKeys Names of exam data keys to clear.
	 */
	const resetExamData = (...examDataKeys: ExamDataStateKey[]) =>
		examDataKeys.length > 0
			? setExamData({
					...examData,
					...examDataKeys.reduce(
						(clearedExamData: Partial<ExamData>, name) => ({
							...clearedExamData,
							[name]: initialExamDatum,
						}),
						{},
					),
			  })
			: setExamData(initialExamData);

	const setInitialExamWindow = React.useCallback(() => {
		if (examWindowOptions?.length) {
			change(EXAMWINDOW_FIELDNAME, examWindowOptions[0].value);
		}
	}, [change, EXAMWINDOW_FIELDNAME, examWindowOptions]);

	/**
	 * Completely reset the Redux Form and its calculated options.
	 */
	const resetForm = () => {
		resetExamData();
		reset();
		// Reset CE group when exam window changes
		setCeGroupId(null);
		setStoredIsPpaEnrollment(false);

		if (hasNoExamWindows) {
			setInitialExamWindow();
		}
	};

	const changeExamData = React.useCallback(
		(
			key: ExamDataStateKey,
			loading?: boolean,
			examDataOptions?: ExamDataOption[],
			show?: boolean,
			examDataNote?: string,
		) => {
			setExamData({
				...examData,
				[key]: {
					options:
						examDataOptions === undefined
							? examData[key].options
							: examDataOptions,
					loading: loading === undefined ? examData[key].loading : loading,
					show: show === undefined ? examData[key].show : show,
					note: examDataNote === undefined ? examData[key].note : examDataNote,
				},
			});
		},
		[examData],
	);

	/**
	 * Sets program state based on Course Code.
	 */
	const setProgramOptions = React.useCallback(async () => {
		changeExamData('programs', true);
		const programOptions = await getProgramOptions(courseCode, setApiError);
		changeExamData('programs', false, programOptions);
	}, [changeExamData, courseCode, setApiError]);

	/**
	 * Sets testing location and country options based on exam's ID.
	 *
	 * @param {string} examId Selected exam's ID.
	 */
	const setCountryOptions = async (examId: string) => {
		changeExamData('countries', true);
		try {
			const testingLocationData =
				await webApi.getTestingLocationsByContactAndLicense(Number(examId));
			setLocationOptions(testingLocationData);
			changeExamData(
				'countries',
				false,
				getCountryOptions(testingLocationData),
			);
		} catch (e) {
			setApiError(e);
		}
	};

	/**
	 * Sets state options based on location options.
	 */
	const setStateOptions = async () => {
		changeExamData('states', true, [], true);
		const statesOptions = await getStateOptions(locationOptions, setApiError);
		changeExamData('states', false, statesOptions, true);
	};

	/**
	 * Sets center options based on country and optional US state.
	 *
	 * @param {string} country Country code value.
	 * @param {string=} usSate  US State code value.
	 */
	const setCenterOptions = (country: string, usSate?: string) =>
		changeExamData(
			'centers',
			false,
			getTestingCenterOptions(locationOptions, country, usSate),
		);

	/**
	 * Set center note based on siteId selection.
	 *
	 * @param {string} siteId Site ID of testing center location.
	 */
	const setCenterNote = (siteId: string) =>
		changeExamData(
			'centers',
			false,
			undefined,
			undefined,
			getTestingLocationNote(centers.options, siteId),
		);

	/**
	 * Event handler to automate exam selection if exam has only one option.
	 *
	 * @event
	 */
	const setExamSelection: React.ChangeEventHandler<HTMLInputElement> = (
		event,
	) => {
		const { value } = event.target;
		if (!TestingLocations.guard(value)) {
			return;
		}

		const examOption = examOptions[value];
		const testingLocation = TESTING_LOCATION_FIELDS[value];

		if (examOption.options?.length === 1) {
			const examId = examOption.options[0].value;
			change(testingLocation.EXAM_FIELDNAME, examId);

			if (value === 'ONSITE' && !countries.options.length) {
				setCountryOptions(examId);
			}
		}
	};

	/**
	 * Event handler for Redux form item onChange.
	 *
	 * @event
	 */
	const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (
		event,
	) => {
		const { id: fieldName, value, name: group } = event.target;
		const { ONSITE } = TESTING_LOCATION_FIELDS;

		if (group === TESTING_LOCATION_FIELDNAME) {
			return setExamSelection(event);
		}

		switch (fieldName) {
			case EXAMWINDOW_FIELDNAME:
				resetForm();
				return setProgramOptions();

			case ONSITE.EXAM_FIELDNAME:
				resetExamData('countries', 'states', 'centers');
				resetFields(
					ONSITE.COUNTRY_FIELDNAME,
					ONSITE.STATE_FIELDNAME,
					ONSITE.CENTER_FIELDNAME,
					ONSITE.PROGRAM_FIELDNAME,
				);
				// Reset CE group when exam selection changes
				setCeGroupId(null);

				return setCountryOptions(value);

			case ONSITE.COUNTRY_FIELDNAME:
				resetExamData('states', 'centers');
				resetFields(
					ONSITE.STATE_FIELDNAME,
					ONSITE.CENTER_FIELDNAME,
					ONSITE.PROGRAM_FIELDNAME,
				);

				return value === DOMESTIC_COUNTRY_VALUE
					? setStateOptions()
					: setCenterOptions(value);

			case ONSITE.STATE_FIELDNAME:
				resetExamData('centers');
				resetFields(ONSITE.CENTER_FIELDNAME, ONSITE.PROGRAM_FIELDNAME);

				return setCenterOptions(DOMESTIC_COUNTRY_VALUE, value);

			case ONSITE.CENTER_FIELDNAME:
				resetFields(ONSITE.PROGRAM_FIELDNAME);
				return setCenterNote(value);

			default:
				return null;
		}
	};

	/**
	 * Handles redux form submission. Will only trigger if redux form is valid.
	 *
	 * @param {FormValues} values List of submitted form values.
	 */
	const handleSubmission = async (values: FormValues) => {
		// EXPPA-25: Require explicit CE selection on last day when CE is required/eligible
		// Since FormContainer has noValidate, HTML5 required won't work - must validate here
		// This prevents Enter-key or programmatic submit bypassing the UI button disable
		if (
			hasPpaCoverage &&
			selectedWindowData.isLastDay &&
			(ceRequired || ppaItem?.ceEligible) &&
			!values.ceRequested
		) {
			const errorMessage =
				'You must select whether you want to file for CE before enrolling. Please select "No" to continue enrollment without CE credit.';
			setSubmitError({ detail: errorMessage });
			throw new SubmissionError({ _error: errorMessage });
		}

		// EXPPA-25: Early check to prevent CE=YES submission on last day
		// This prevents race conditions where button might briefly be enabled
		const ceRequested = values.ceRequested || false;
		if (hasPpaCoverage && selectedWindowData.isLastDay && ceRequested === 'YES') {
			const errorMessage =
				'Due to state CE requirements, you cannot request CE for this exam on the last day of the testing window. To receive CE, please enroll in the next series. Otherwise, you may continue enrollment without requesting CE by selecting "No".';
			setSubmitError({ detail: errorMessage });
			throw new SubmissionError({ _error: errorMessage });
		}

		const locationType = values[TESTING_LOCATION_FIELDNAME];

		if (!TestingLocations.guard(locationType)) {
			return;
		}

		const locationFields = TESTING_LOCATION_FIELDS[locationType];
		const {
			EXAM_FIELDNAME,
			PROGRAM_FIELDNAME,
			COUNTRY_FIELDNAME,
			STATE_FIELDNAME,
			CENTER_FIELDNAME,
		} = locationFields;

		const itemId = parseInt(values[EXAM_FIELDNAME], 10);
		const series = values[EXAMWINDOW_FIELDNAME];
		const centerCode = CENTER_FIELDNAME ? values[CENTER_FIELDNAME] : null;
		const programCode = values[PROGRAM_FIELDNAME];
		// ceRequested already declared at top of function

		// EXPPA-25: Get selected exam window to validate date restrictions
		const selectedWindow = examWindowOptions?.find(
			({ value }) => value === series,
		);
		const currentDate = getCurrentDateTime().toISODate() ?? '';
	// Normalize endDate to ISO date string for comparison
	const normalizedEndDate = selectedWindow?.endDate
		? parseDateWithoutTimezone(selectedWindow.endDate)?.toISODate() ?? ''
		: '';
	// Check if today is the last day of the window
	const isLastDayOfWindow = !!(
		normalizedEndDate &&
		currentDate &&
		currentDate === normalizedEndDate
	);
	// Check if the window has already closed (current date is after end date)
	const isWindowClosed = !!(
		normalizedEndDate &&
		currentDate &&
		currentDate > normalizedEndDate
	);

	// Prevent ALL enrollments if the exam window has closed
	if (isWindowClosed) {
			const errorMessage =
				'The exam window for this course has closed. Please select a future exam window to enroll.';
			setSubmitError({ detail: errorMessage });
			throw new SubmissionError({ _error: errorMessage });
		}

		// EXPPA-25: Validate CE enrollment on last day of exam window
		// VE enrollments with CE credit are NOT allowed on the last day due to +1 day access requirement
		// Note: Users can opt out of CE even if ceRequired=true by selecting "No" for CE on the last day
		if (hasPpaCoverage && isLastDayOfWindow) {
			if (ceRequested === 'YES') {
				const errorMessage =
					'Due to state CE requirements, you cannot request CE for this exam on the last day of the testing window. To receive CE, please enroll in the next series. Otherwise, you may continue enrollment without requesting CE.';
				setSubmitError({ detail: errorMessage });
				throw new SubmissionError({ _error: errorMessage });
			}
		}

		// EXPPA-25/EXPPA-38: PPA enrollment path (skip for retakes - they use cart path)
		if (hasPpaCoverage && !isPpaRetake && onPpaEnroll && ppaLicenseId) {
			// Validate required fields for PPA enrollment
			const missingFields: string[] = [];

			// Validate CE configuration if CE is requested (user selected "Yes")
			if (ceRequested === 'YES' && !ceGroupId) {
				const errorMessage = 'CE (Continuing Education) configuration is required. Please click "Enter your CE licensing information" to configure your CE credits before enrolling.';
				setSubmitError({ detail: errorMessage });
				throw new SubmissionError({ _error: errorMessage });
			}

			if (programRequired && !programCode) {
				missingFields.push('Program');
			}

			if (missingFields.length > 0) {
				const errorMessage = `The following required fields are missing: ${missingFields.join(', ')}. Please complete all required fields to enroll in this exam.`;
				setSubmitError({ detail: errorMessage });
				throw new SubmissionError({ _error: errorMessage });
			}

			try {
				// EXPPA-25: Ensure groupId is cleared if CE is not requested
				const finalCeGroupId = ceRequested === 'YES' ? ceGroupId : null;

				const enrollmentPayload = {
					prepaidAgreementId: ppaLicenseId,
					licenseId: ppaLicenseId,
					itemId,
					targetSeries: series,
					...(programCode && { programCode }),
					ceRequested: ceRequested === 'YES',
					// Use 'groupId' (not 'ceGroupId') to match backend/other enrollment flows
					// Only include groupId if CE is explicitly requested AND configuration exists
					...(ceRequested === 'YES' && finalCeGroupId && { groupId: finalCeGroupId }),
				};

				const result = await onPpaEnroll(enrollmentPayload);

				// Check for API errors
				if (result?.type === 'API_ERROR') {
					const errorDetail = result.payload?.detail || 'Enrollment failed';

					// Extract error from proxy response structure
					// Try multiple possible paths for proxy-wrapped errors
					const proxyError =
					// Nested proxy response
					result.payload?.data?.data?.response ||
					// Axios error response
					result.payload?.response?.data ||
					// Direct data
					result.payload?.data ||
					// Fallback to payload itself
					result.payload;

					const errorCode = proxyError?.errorCode;
					const errorMessage = proxyError?.message;

					// EXPPA-25: Handle deadline error based on whether window is actually closed
					// The API returns 'Exam_reg_deadline_passed' in two scenarios:
					// 1. On last day with CE=YES (correctly blocked per business rules)
					// 2. On last day with CE=NO (incorrectly blocked - this should be allowed)
					// 3. After last day (correctly blocked - window is closed)
					if (errorCode === 'Exam_reg_deadline_passed') {
						// Check if window is actually closed (date is AFTER end date)
						const errorWindow = examWindowOptions?.find(
							({ value }) => value === series,
						);
						const errorCheckDate = getCurrentDateTime().toISODate() ?? '';
						const errorWindowEndDate = errorWindow?.endDate ? parseDateWithoutTimezone(errorWindow.endDate)?.toISODate() ?? '' : '';
						const hasWindowClosed = errorWindowEndDate && errorCheckDate > errorWindowEndDate;

						if (hasWindowClosed) {
							// Window is actually closed - show the deadline message
							const deadlineError = errorMessage || 'You have passed the deadline to register for this exam.';
							setSubmitError({ detail: deadlineError });
							throw new SubmissionError({ _error: deadlineError });
						} else if (isLastDayOfWindow && ceRequested !== 'YES') {
							// Last day without CE - allow enrollment to proceed normally
							// Do nothing, let the form submit
						} else {
							// Last day with CE=YES or other cases
							const unavailableError = 'Enrollment on the last day of the exam window is currently unavailable. Please enroll in the next exam series or contact support at (800) 295-9010 for assistance.';
							setSubmitError({ detail: unavailableError });
							throw new SubmissionError({ _error: unavailableError });
						}
					}

					let displayErrorMessage = '';

					// Check for "Already Enrolled" error
					if (errorCode === 'Already_Enrolled') {
						displayErrorMessage = errorMessage || 'You are already enrolled in this exam.';
					} else if (
						// Check for validation errors from backend
						errorDetail.includes('CE') ||
						errorDetail.includes('continuing education')
					) {
						displayErrorMessage =
							'CE (Continuing Education) is required for this exam. Please select CE before enrolling.';
					} else if (
						errorDetail.includes('program') ||
						errorDetail.includes('Program')
					) {
						displayErrorMessage =
							'Program selection is required for this exam. Please select a program before enrolling.';
					} else if (errorMessage) {
						// Use proxy error message if available
						displayErrorMessage = errorMessage;
					} else {
						displayErrorMessage = errorDetail;
					}

					setSubmitError({ detail: displayErrorMessage });
					throw new SubmissionError({ _error: displayErrorMessage });
				}

				// Success - show success state
				setStoredTitle(selectedTitle);
				setStoredIsPpaEnrollment(true);
				setLabels({
					examWindow: getLabel(
						values,
						EXAMWINDOW_FIELDNAME,
						examWindowOptions || [],
					),
					locationType: getLabel(
						values,
						TESTING_LOCATION_FIELDNAME,
						Object.values(examOptions),
					),
					exam: getLabel(values, EXAM_FIELDNAME, examOptions[locationType].options),
					program: getLabel(values, PROGRAM_FIELDNAME, programs.options),
					country: getLabel(values, COUNTRY_FIELDNAME, countries.options),
					state: getLabel(values, STATE_FIELDNAME, states.options),
					center: getLabel(values, CENTER_FIELDNAME, centers.options),
				});

				// Clear form and stop submitting
				reset();
				// Enrollment complete - form will show success overlay
				return;
			} catch (err: any) {
				// Handle any unexpected errors
				if (err instanceof SubmissionError) {
					throw err;
				}
				const errorMessage =
					err?.message ||
					'An unexpected error occurred during enrollment. Please try again.';
				setSubmitError({
					detail: errorMessage,
				});
				throw new SubmissionError({ _error: errorMessage });
			}
		}

		// Existing cart-based purchase path
		const registration = { centerCode, examDate, programCode };

		const { type, payload } =
			locationType === 'VIRTUAL'
				? await addToCart({
						itemId,
						series,
						...(programCode && { program: programCode }),
						...(ceRequested && { ceRequested: ceRequested === 'YES' }),
						// Use 'groupId' for cart-based flow as well
						...(ceRequested === 'YES' && ceGroupId && { groupId: ceGroupId }),
				  })
				: await addExamToCart({ itemId, series, registration });

		if (type === 'API_ERROR') {
			const errorMessage =
				payload?.detail || payload?.message || 'Failed to add exam to cart.';
			setSubmitError(payload);
			throw new SubmissionError({ _error: errorMessage });
		}

		await gtmAddToCart(parseInt(values[EXAM_FIELDNAME], 10), programCode);

		await getCart('inline');

		setStoredTitle(selectedTitle);
		setStoredIsPpaEnrollment(false);
		setLabels({
			examWindow: getLabel(
				values,
				EXAMWINDOW_FIELDNAME,
				examWindowOptions || [],
			),
			locationType: getLabel(
				values,
				TESTING_LOCATION_FIELDNAME,
				Object.values(examOptions),
			),
			exam: getLabel(values, EXAM_FIELDNAME, examOptions[locationType].options),
			program: getLabel(values, PROGRAM_FIELDNAME, programs.options),
			country: getLabel(values, COUNTRY_FIELDNAME, countries.options),
			state: getLabel(values, STATE_FIELDNAME, states.options),
			center: getLabel(values, CENTER_FIELDNAME, centers.options),
		});

		reset();
	};

	// Dispatch immediate exam window selection if No Window is set.
	React.useEffect(() => {
		if (hasNoExamWindows) {
			setInitialExamWindow();
			setProgramOptions();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const COURSE_NOTE = note ? (
		<Notification className="u-margin-bottom">
			{/* eslint-disable-next-line react/no-danger */}
			<div dangerouslySetInnerHTML={{ __html: note }} />
		</Notification>
	) : null;

	/*
	 * Form container with an exam window Select, and a TestingLocationForm form component.
	 *
	 * Upon successful form submission, an PurchaseExamOverlay appears.
	 */
	return (
		<div
			className="purchase-tabs__panel"
			id={panelId}
			role={!isSingleTab ? 'tabpanel' : ''}
			aria-labelledby={!isSingleTab ? panelId : ''}
			hidden={hidden}
		>
			{store.auth.isAuthenticated ? (
				<>
					<p>{description}</p>
					{COURSE_NOTE}

					{/* EXPPA-38: Always show form to allow exam selection changes */}
					{/* CTA area shows "Enroll Now"/"Add to Cart" (if eligible) or "Already enrolled" message */}
					<FormContainer
						isRedux
						className={classNames('purchase-tabs__purchase-form', {
							'has-loader': submitting,
						})}
						onSubmit={handleSubmit(handleSubmission)}
					>
						{submitting && <Loader isVeiled />}

						<ul className="u-list-unstyled">
							<li
								className={classNames('form__item', {
									'u-hidden': hasNoExamWindows,
								})}
							>
								<Select
									id={EXAMWINDOW_FIELDNAME}
									name={EXAMWINDOW_FIELDNAME}
									label="Select an Anticipated Exam Window"
									options={examWindowOptions}
									validate={hasNoExamWindows ? [] : [examWindowRequired]}
									tooltipMessage="We regularly update our content to provide you
            with the most up-to-date skills. Select your expected exam
            window to ensure you purchase the most current materials."
									onChange={handleOnChange}
								/>
							</li>

							{/* EXPPA-25: CE field for PPA exam enrollment */}
							{hasPpaCoverage && (ceRequired || ppaItem?.ceEligible) && (
								<li className="form__item">
									<fieldset className="input__wrapper">
										<legend className="input__label">
											Do you want to file for CE?{(ceRequired || ppaItem?.ceEligible) && '*'}
										</legend>

										{/* Warning for closed or last day of exam window */}
										{selectedWindowData.isWindowClosed && (
											<Notification className="u-margin-md-bottom">
												The exam window for this course has closed. Please select a future exam window to enroll.
											</Notification>
										)}
										{!selectedWindowData.isWindowClosed && selectedWindowData.isLastDay && (
											<Notification className="u-margin-md-bottom">
												Due to state CE requirements, you cannot request CE for this exam on the last day of
												the testing window. To receive CE, please enroll in the next series. Otherwise, you
												may continue enrollment without requesting CE.
											</Notification>
										)}

										<div className="custom__rb-group--horizontal custom__rb-group--horizontal--nowrap">
											<div className="custom__rb">
												<input
													type="radio"
													id={`ce_yes_purchase_${selectedExam}`}
													name="ceRequested"
													value="YES"
													checked={formValues?.ceRequested === 'YES'}
													onChange={(e) => change('ceRequested', e.target.value)}
													required={ceRequired || ppaItem?.ceEligible}
													disabled={selectedWindowData.isLastDay}
												/>
												<label htmlFor={`ce_yes_purchase_${selectedExam}`}>Yes</label>
											</div>

											<div className="custom__rb">
												<input
													type="radio"
													id={`ce_no_purchase_${selectedExam}`}
													name="ceRequested"
													value="NO"
													checked={formValues?.ceRequested === 'NO' || false}
													onChange={(e) => {
														change('ceRequested', e.target.value);
														// EXPPA-25: Clear CE configuration when user selects NO
														setCeGroupId(null);
													}}
													required={ceRequired || ppaItem?.ceEligible}
												/>
												<label htmlFor={`ce_no_purchase_${selectedExam}`}>No</label>
											</div>
										</div>
									</fieldset>

									{/* Show CE Modal when Yes is selected */}
									{formValues?.ceRequested === 'YES' && selectedExam && (
										<div className="modal-activator__content">
											<CeModal
												linkText="Enter your CE licensing information"
												itemId={parseInt(selectedExam, 10)}
												licenseNumber={ppaLicenseId?.toString()}
												setGroupId={setCeGroupIdNormalized}
											/>
										</div>
									)}
								</li>
						)}

							<li className="form__item">
								{showTestingLocationForm && (
									<TestingLocationForm
										className="purchase-tabs__exams u-padding-bottom"
										examOptions={examOptions}
										examData={examData}
										formValues={formValues}
										handleOnChange={handleOnChange}
										id={TESTING_LOCATION_FIELDNAME}
										isSrOnly
										label="Select an Exam"
										name={TESTING_LOCATION_FIELDNAME}
										required
										testingLocationFields={TESTING_LOCATION_FIELDS}
										validate={[examTestingLocationRequired]}
										component={TestingLocationFormComponent}
										programRequired={programRequired}
									/>
								)}
							</li>
						</ul>

						{hasMatchingExamWindow && (
							<Notification className="u-margin-bottom">
								The window you selected to take your exam does not match the
								anticipated exam window of your materials.
							</Notification>
						)}

						{/* EXPPA-38: Show notification for PPA retake restrictions */}
						{isPpaRetake && selectedExam && (
							<Notification className="u-margin-bottom">
								You cannot retake the exam under the Prepaid Agreement,
								please Add To Cart and check out with another purchase
								method.
							</Notification>
					)}

						{/* Show error notification for PPA enrollment failures */}
						{hasPpaCoverage && submitFailed && submitError && (
							<Notification className="u-margin-bottom">
								{(submitError as any)?.detail || 'An error occurred during enrollment. Please try again.'}
							</Notification>
						)}

						{/* EXPPA-38: Show CTA buttons or enrolled message based on enrollment and failure status */}
						{/* Priority 1: Failed exams always show Add to Cart (repurchase flow) */}
						{hasFailedExam && selectedExam && (
							<AddCartButton
								className={classNames({
									'btn--disabled': !valid,
								})}
								product={selectedTitle}
								price={priceToDisplay}
								error={submitFailed && submitError}
								isInForm
							/>
						)}
						{/* Priority 2: Successfully enrolled (no failure) shows enrolled message */}
						{!hasFailedExam && selectedExam && enrolled && (
							<Notification className="u-margin-top">
								<span>
									You&apos;ve enrolled in this course. Go to{' '}
									<a href={store.auth.myCoursesUrl}>My Courses</a> to access it.
								</span>
							</Notification>
						)}
						{/* Priority 3: PPA coverage available shows Enroll Now */}
						{!hasFailedExam && !(selectedExam && enrolled) && hasPpaCoverage && (
							<PrimaryButton
								className={classNames({
									'btn--disabled':
										!valid ||
										submitting ||
										(selectedWindowData.isLastDay && formValues?.ceRequested === 'YES'),
								})}
								type="submit"
								disabled={
									!valid ||
									submitting ||
									(selectedWindowData.isLastDay && formValues?.ceRequested === 'YES')
								}
							>
								{submitting ? 'Enrolling...' : 'Enroll Now'}
							</PrimaryButton>
						)}
						{/* Priority 4: Default to Add to Cart for regular purchases */}
						{!hasFailedExam && !(selectedExam && enrolled) && !hasPpaCoverage && (
							<AddCartButton
								className={classNames({
									'btn--disabled': !valid,
								})}
								product={selectedTitle}
								price={priceToDisplay}
								error={submitFailed && submitError}
								isInForm
							/>
						)}
					</FormContainer>

					<PurchaseExamOverlay
						hasMatchingCartItem={hasMatchingCartItem}
						labels={labels}
						onClose={resetForm}
						onSelect={onSelect}
						show={submitSucceeded}
						title={storedTitle}
						isSingleForm={isSingleTab}
						setContainerHeight={setContainerHeight}
						isPpaEnrollment={storedIsPpaEnrollment}
						myCoursesUrl={store.auth.myCoursesUrl}
					/>
				</>
			) : (
				<>
					{COURSE_NOTE}
					<p className="u-margin-hug-top u-font-family-text">
						<strong>Sign in or create an account to purchase your exam.</strong>{' '}
						{description}
					</p>
					<LoginButton courseCode={courseCode} loginUrl={store.auth.loginUrl} />
				</>
			)}
		</div>
	);
};

export const PurchaseTabExam = reduxForm<{}, PropPurchaseTabExam>({})(
	PurchaseTabExamRaw,
);
